import uniqid from 'uniqid';
import { pickLocalFiles } from '@shakkerai/util-file';
import React, { useCallback, useMemo, forwardRef } from 'react';
import { MAX_FILE_SIZE } from '@shakkerai/pro-constants';
import { notifyError, notifyWarn } from '@shakkerai/pro-components';
import { translate } from '@shakkerai/util-i18n';
import { Loader, Overlay } from '@mantine/core';
import { useDisclosure, useHover } from '@mantine/hooks';
import { compressLocalImage, convert2CompressedUrl } from '@shakkerai/util-image';
import { uploadImage } from '@shakkerai/pro-utils';

/**
 * 纯粹的文件上传工具
 * 提供图片的上传，展示，重新上传能力
 * @param {*} param0
 * @returns
 */
export const ImageUploadInput = forwardRef(
  (
    { uploadedImage, onImageUploaded, emptyText = '', disabled = false, height, imageDomId = 'colorUpload' },
    imgRef,
  ) => {
    const [uploading, loadingHandlers] = useDisclosure(false);

    const uploadControlNetImage = useCallback(
      async (file) => {
        try {
          loadingHandlers.open();
          const { file: compressedFile, width, height, compressed } = await compressLocalImage(file, 2048, 2048);

          if (compressed) {
            notifyWarn(translate('img_resize_toast'));
          }

          const uploadResult = await uploadImage(compressedFile, false, 'img_reference').catch(() => ({ url: null }));

          loadingHandlers.close();
          if (!uploadResult || !uploadResult.url) {
            notifyError(translate('upload_error'), translate('upload_fail_toast'));
            return;
          }

          const { url } = uploadResult;
          const sourceImage = { id: uniqid(), url, width, height };

          onImageUploaded(sourceImage);

          return sourceImage;
        } catch (error) {
          console.error(error);
        }
      },
      [loadingHandlers, onImageUploaded],
    );

    const handlePickLocalImage = useCallback(async () => {
      if (disabled) return;

      const [file] = await pickLocalFiles(['jpeg', 'jpg', 'png', 'webp']);

      if (file.size > MAX_FILE_SIZE) {
        notifyError(translate('common_operation_failed'), translate('img_size_toast'));
        return false;
      }

      uploadControlNetImage(file);
    }, [disabled, uploadControlNetImage]);

    const emptyNode = useMemo(
      () => (
        <>
          <span className="text-sm text-[#7f7f7f]">{emptyText}</span>
          <button
            disabled={disabled}
            onClick={disabled ? undefined : handlePickLocalImage}
            className="w-32 h-[34px] mt-3.5 bg-white border border-[#ededed] hover:border-[#cdcdcd] rounded text-[#2d2d2d] text-xs font-grotesk"
          >
            {translate('cn_face_upload_image')}
          </button>
        </>
      ),
      [disabled, handlePickLocalImage, emptyText],
    );

    const uploadingNode = useMemo(
      () => (
        <div className="flex items-center justify-center w-full">
          <Loader size={18} />
        </div>
      ),
      [],
    );

    const { hovered, ref } = useHover();

    const imageShowNode = useMemo(() => {
      if (uploadedImage) {
        return (
          <div className="relative flex items-center justify-center w-full h-full">
            <img
              src={convert2CompressedUrl(uploadedImage.url)}
              ref={imgRef}
              draggable={false}
              crossOrigin="anonymous"
              id={imageDomId}
              className="w-full h-full object-contain"
            />
            {hovered && (
              <Overlay color="#000" backgroundOpacity={0.7}>
                <div className="flex w-full h-full items-center justify-center">
                  <div
                    onClick={handlePickLocalImage}
                    className="py-2 px-10 rounded-[2px] border-solid border-white border-[1px] cursor-pointer text-[white] text-[12px]"
                  >
                    {translate('cn_face_upload_image')}
                  </div>
                </div>
              </Overlay>
            )}
          </div>
        );
      } else {
        return null;
      }
    }, [uploadedImage, hovered, handlePickLocalImage]);

    const controlHub = useMemo(() => {
      if (uploading) {
        return uploadingNode;
      } else if (uploadedImage?.url) {
        return imageShowNode;
      } else {
        return emptyNode;
      }
    }, [uploading, uploadingNode, uploadedImage, imageShowNode, emptyNode]);

    return (
      <div
        className="flex items-center flex-col justify-center h-[188px] w-full bg-[#F8F9FA] overflow-hidden"
        style={{
          height: height || '188px',
        }}
        ref={ref}
      >
        {controlHub}
      </div>
    );
  },
);
